import React from "react";
import { Package, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

type InventoryStatusCardProps = {
    title?: string;
    data?: {
        totalProducts: number;
        inStockProducts: number;
        lowStockProducts: number;
        outOfStockProducts: number;
    };
};

const InventoryStatusCard: React.FC<InventoryStatusCardProps> = ({
    title = "Inventory Status",
    data,
}) => {
    // Standard safe fallbacks to map incoming backend data properties
    const total = data?.totalProducts ?? 0;
    const inStock = data?.inStockProducts ?? 0;
    const lowStock = data?.lowStockProducts ?? 0;
    const outOfStock = data?.outOfStockProducts ?? 0;

    // Mapping your raw properties into your exact original array structure inline
    const inventoryItems = [
        {
            title: "Total Products",
            count: `${total.toLocaleString()} Products`,
            icon: Package,
            iconBg: "bg-blue-100",
            iconColor: "text-blue-600",
        },
        {
            title: "In Stock Products",
            count: `${inStock.toLocaleString()} Products`,
            icon: CheckCircle2,
            iconBg: "bg-green-100",
            iconColor: "text-green-600",
        },
        {
            title: "Low Stock Products",
            count: `${lowStock.toLocaleString()} Products`,
            icon: AlertTriangle,
            iconBg: "bg-yellow-100",
            iconColor: "text-yellow-600",
        },
        {
            title: "Sold Out Products",
            count: `${outOfStock.toLocaleString()} Products`,
            icon: XCircle,
            iconBg: "bg-red-100",
            iconColor: "text-red-600",
        },
    ];

    return (
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 w-full h-full">

            {/* HEADER */}
            <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-logoHeading">
                    {title}
                </h2>
            </div>

            {/* LIST */}
            <div className="space-y-4">
                {inventoryItems.map((item, index) => {
                    const Icon = item.icon;

                    return (
                        <div
                            key={index}
                            className="flex items-center gap-4 border border-gray-100 rounded-2xl px-4 py-2"
                        >
                            {/* ICON */}
                            <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center ${item.iconBg}`}
                            >
                                <Icon
                                    size={18}
                                    className={item.iconColor}
                                />
                            </div>

                            {/* CONTENT */}
                            <div>
                                <h3 className="text-sm font-semibold text-logoHeading">
                                    {item.title}
                                </h3>

                                <p className="text-xs text-gray-500 mt-1">
                                    {item.count}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default InventoryStatusCard;